import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import {
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Switch from '__components/Kizen/Switch';
import Loader from '__components/Kizen/Loader';
import type { StepSubmitRef } from './AddCardModal';

type SetupFormProps = {
  submitRef: StepSubmitRef;
  existingIds: string[];
  defaultCountry?: string | null;
  defaultPostalCode?: string | null;
  onSuccess: (paymentMethodId: string, makePrimary: boolean) => Promise<void>;
  showMakePrimary?: boolean;
};

export const SetupForm = (props: SetupFormProps) => {
  const {
    submitRef,
    existingIds,
    defaultCountry,
    defaultPostalCode,
    onSuccess,
    showMakePrimary = true,
  } = props;
  const { t } = useTranslation();

  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState<string>();
  const [makePrimary, setMakePrimary] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const submittingRef = useRef(false);

  const paymentElementOptions = useMemo(
    () => ({
      defaultValues: {
        billingDetails: {
          address: {
            country: defaultCountry ?? undefined,
            postal_code: defaultPostalCode ?? undefined,
          },
        },
      },
    }),
    [defaultCountry, defaultPostalCode]
  );

  const handleSubmit = useCallback(async () => {
    // Stripe js hasn't been loaded yet
    if (!stripe || !elements || submittingRef.current) {
      return;
    }

    setErrorMessage(undefined);
    submittingRef.current = true;
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.href}?success=true&makePrimary=${makePrimary}&existing=${existingIds.join(',')}`,
        },
        redirect: 'if_required',
      });

      if (error) {
        if (error.type !== 'validation_error') {
          setErrorMessage(error.message);
        }
      } else if (setupIntent.payment_method) {
        const paymentMethod = setupIntent.payment_method;
        await onSuccess(
          typeof paymentMethod === 'string' ? paymentMethod : paymentMethod.id,
          makePrimary
        );
      }
    } finally {
      submittingRef.current = false;
    }
  }, [stripe, elements, makePrimary, existingIds, onSuccess]);

  const handleFormSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      handleSubmit();
    },
    [handleSubmit]
  );

  useEffect(() => {
    submitRef.current = handleSubmit;
  }, [submitRef, handleSubmit]);

  return (
    <form onSubmit={handleFormSubmit} className="kds relative flex-1">
      {isReady ? null : (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader loading paddingTop="0" />
        </div>
      )}
      <div className={isReady ? undefined : 'invisible'}>
        {errorMessage ? (
          <>
            <Typography color="feedback-toast-error">{errorMessage}</Typography>
            <Spacer size={20} />
          </>
        ) : null}
        <div>
          <Typography weight="semibold">{t('Payment Method')}</Typography>
          <Spacer size={15} />
          <PaymentElement
            options={paymentElementOptions}
            onReady={() => setIsReady(true)}
          />
          {showMakePrimary ? (
            <div className="pt-4">
              <Switch
                label={t('Make this the primary payment method')}
                textPlacement="regular-top"
                value="true"
                onChange={(e) => setMakePrimary(e.target.checked)}
                disabled={false}
                checked={makePrimary}
              />
            </div>
          ) : null}
        </div>
      </div>
      <button type="submit" hidden tabIndex={-1} aria-hidden="true" />
    </form>
  );
};
